/**
 * ClipboardScreen — the clipboard destination (web + extension).
 *
 * Successor to `ClipDock`. The dock had to be a collapsed bar first and a
 * panel second, because it lived pinned under the notification list; as a
 * bottom-nav destination it owns the whole area, so the collapsed state, the
 * expand/collapse toggle and the new-clip pulse all disappear.
 *
 * 2026-08-03 redesign (docs/superpowers/specs/2026-08-03-clipboard-screen-
 * redesign-design.md, R1/R7):
 *   - **Docked composer.** The RECENT list scrolls internally; the composer
 *     stays pinned to the bottom of the destination, so the newest clip and
 *     the place you type never move — mirrors Android's clipboard manager.
 *   - **Designed empty state.** A single quiet line reads as broken at
 *     desktop height, so the empty list gets a title + body block instead.
 *     The composer stays docked and usable underneath it.
 *
 * 2026-08-05 card-language redesign (docs/superpowers/specs/2026-08-05-
 * clipboard-card-language-design.md, C6/C7): **one-click send.** The composer
 * is `[paste] [input] [sensitive lock] [send]`; Send broadcasts immediately —
 * `SendConfirmPopover` is deleted. It reversed the 2026-08-03 design's
 * routing of Send through a confirm step, on grounds a hardware audit
 * falsified: the popover's "Send to N devices" gate before plaintext fanned
 * out was never real (both hosts passed a hardcoded `deviceCount={1}`
 * literal), and its `From clipboard · N characters` preview lied whenever the
 * text was typed rather than pasted. The popover's one real function —
 * setting `sensitive` — moves into the composer's lock toggle (`markSensitive`
 * label) and resets to off after every send (C7): a sticky privacy flag fails
 * both ways, mislabeling ordinary clips if left on, or silently sending the
 * next password unmasked if left off.
 *
 * The paste control still only fills the draft, never sends by itself — what
 * leaves the device is always something visible in the field first, whether
 * typed or pasted. One click no longer sits behind a confirm step, so the
 * draft is only cleared on a send that actually delivered — a failed or
 * fully-undelivered broadcast leaves the typed/pasted text in place, since
 * it is frequently un-retypeable (a long token, a generated password, a URL
 * copied from another device). See `handleComposerSend`.
 *
 * The dock's top-level Copy button is gone: every `ClipCard` carries its own,
 * so a screen-level one only duplicated the first row's affordance.
 *
 * Screen chrome (C8/C9): "Clear all" is now the notification toolbar's
 * neutral icon+label treatment (`variant="ghost"`, `leadingIcon={Trash01}`)
 * instead of `negative-ghost` — at rest it was the only red pixel on an
 * idle screen, outranking the content it sat above. The two-step confirm is
 * unchanged; only the RESTING trigger lost its red, so the destructive
 * commit button in the confirm step stays `variant="negative"`. The list is
 * also now grouped into Today / Yesterday / older-day sections via the pure
 * `groupByDay` helper, headers styled like
 * `SidePanelNotificationList`'s `.dateLabel` ("Today" section label). The
 * header is suppressed when there is only one group — mirroring
 * `SidePanelNotificationList`'s own `timelineBuckets.length > 1` gate on its
 * date separator — since an always-on lone "Today" header where the
 * notification list shows none is exactly the Notification/clipboard visual
 * divergence this redesign exists to close, and it's the common case (most
 * users only have today's clips), not an edge case. The grouping is
 * memoized (`[clips, dayTick]`) and re-derived at the calendar-day boundary
 * via a midnight `setTimeout` tick — see `dayTick` below — mirroring
 * `useNotificationListView`'s identical pattern, so a panel left open
 * across midnight doesn't keep filing yesterday's clips under "Today".
 *
 * Round 3 post-dogfood refinements (docs/superpowers/specs/2026-08-05-
 * clipboard-card-language-design.md §7, D5): reveal is not permanent. This
 * screen owns ONE `visibilitychange` listener (`hiddenTick` below) that
 * bumps every `ClipCard`'s `resetSignal` prop when the document becomes
 * hidden, re-masking whatever was revealed — a single click that survives
 * a tab switch defeats the point of privacy mode. Deliberately ONE listener
 * pair here rather than one per card. Fix round (post-round-3 dogfood):
 * `visibilitychange` alone missed an unfocused-but-visible window and could
 * be inert on the side panel (D5's own motivating case — Chrome registers
 * it globally, so a browser-tab switch doesn't necessarily mark it
 * `hidden`), so `useHasFocus` (mirrored from `apps/extension/src/sidepanel/
 * useHasFocus.ts` — see that file for why) also re-masks on ordinary
 * `window` blur. The rest of round 3 (header layout, contrast, masked-hint
 * plumbing) lives entirely inside `ClipCard` itself and needs no wiring
 * from this screen.
 *
 * SECURITY: clip content (in or out) is never logged; failures surface a tag
 * only, via the host adapter. `groupByDay` and `dayGroupLabel` below
 * read only `clip.timestamp` — never content.
 */
import { Clipboard, Lock01, LockUnlocked01, Trash01 } from "@untitledui/icons";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHasFocus } from "../../hooks/useHasFocus";
import { type DayGroup, groupByDay } from "../../utils/groupByDay";
import { Button } from "../Button";
import { ClipCard, type ClipCardLabels } from "../ClipCard";
import { IconButton } from "../IconButton";
import { Input } from "../Input";
import { Text } from "../Text";
import type { ClipboardCapabilities, DockClip } from "./ClipboardCapabilities";
import styles from "./ClipboardScreen.module.css";

export interface ClipboardScreenLabels {
  card: ClipCardLabels;
  /** aria-label for the composer's leading "paste from clipboard" control. */
  pasteFromClipboard: string;
  /**
   * Accessible name for the composer's sensitive-lock toggle (an IconButton
   * with `aria-pressed`, icon swaps Lock01/LockUnlocked01 by state — same
   * idiom as `PublicModeToggle`'s Eye/EyeOff). One label covers both states;
   * `aria-pressed` conveys which. Marks the NEXT send as `sensitive`; resets
   * to off once that send settles (C7) — see `handleComposerSend`.
   */
  markSensitive: string;
  recentHeading: string;
  /**
   * Day-group section header (C9) for clips from the current calendar day.
   * Gated at render, same fail-closed idiom as every other optional-in-
   * practice label here (`markSensitive`, `clearAll`): an empty string
   * falls back to the formatted date rather than an unlocalized English
   * "Today" — this package isn't scanned by `l10n:check`. See
   * `dayGroupLabel`.
   */
  today: string;
  /** Day-group section header (C9) for clips from the previous calendar day. Same empty-string fallback as `today`. */
  yesterday: string;
  /** Headline of the designed empty state, shown in place of the list when no clips have arrived (R7). */
  emptyTitle: string;
  /** Supporting line beneath `emptyTitle`, explaining what the screen is for. */
  emptyBody: string;
  /** The OS clipboard was readable but held nothing worth pasting. */
  emptyClipboardTip: string;
  /**
   * The OS clipboard could not be read at all — typically a permission or
   * user-gesture refusal. Points the user at the composer, which is already
   * on screen and usable by hand.
   */
  readFailedTip: string;
  sentToast: (sent: number, total: number) => string;
  sentNone: string;
  composerPlaceholder: string;
  composerSend: string;
  /** aria-label for the destination's <section>. */
  sectionLabel: string;
  /**
   * Trigger for the header's clear-all control. Optional so hosts that
   * haven't wired `onClearAll` yet (PR1) keep compiling without a label
   * update. The control renders only when BOTH `onClearAll` AND this label
   * are supplied — a host that wires the callback without a translation
   * gets a visibly missing button instead of an unlocalized English string
   * (this package isn't covered by `l10n:check`).
   */
  clearAll?: string;
  /** Commit button label for the clear-all confirm step. Optional; supply alongside `clearAll` once `onClearAll` is wired. */
  clearAllConfirm?: string;
  /** Cancel button label for the clear-all confirm step. Optional; supply alongside `clearAll` once `onClearAll` is wired. */
  clearAllCancel?: string;
}

export interface ClipboardScreenProps {
  clips: DockClip[];
  capabilities: ClipboardCapabilities;
  labels: ClipboardScreenLabels;
  publicMode?: boolean;
  /** Delete a single clip from history. Threaded to each `ClipCard` as `onDelete`. */
  onDeleteClip?: (id: string) => void;
  /** Clear all clip history. Rendered behind a two-step confirm (destructive + irreversible). */
  onClearAll?: () => void;
  /**
   * Analytics-only hook: fired with the full clip after a `ClipCard`'s copy
   * button successfully writes it to the OS clipboard. Optional so hosts that
   * don't instrument this yet keep compiling. The screen itself never calls
   * an analytics SDK (client-ui stays platform-free) — the host supplies this
   * callback to bridge into its own tracker, mirroring `onDeleteClip`/
   * `onClearAll`'s "callback in, no policy here" shape. Receives the clip
   * (not just its id) because the direction — sent vs received — is the
   * property the host needs and is only known at this call site.
   */
  onClipCopied?: (clip: DockClip) => void;
  /**
   * Analytics-only hook: fired once a send (typed or pasted — the composer
   * is the only send path since the 2026-08-03 redesign, R2) successfully
   * reaches at least one device. Covers every send from this screen now;
   * see `docs/analytics/event-spec.md`.
   */
  onComposerSend?: () => void;
}

/**
 * Resolve a `DayGroup`'s section-header text (C9).
 *
 * `today`/`yesterday` come from `labels` and are gated on truthiness — an
 * empty string (the "label not wired yet" state this package uses
 * throughout, since it has no `l10n:check` backstop) falls back to the
 * formatted date rather than a hardcoded English word. "older" groups
 * always format their date: there's no single label that could stand in
 * for an arbitrary calendar day.
 *
 * The date format mirrors `formatRelativeTime`'s existing precedent in this
 * package literally — `Date.prototype.toLocaleDateString(undefined, {
 * month: "short", day: "numeric" })`, not a manually-constructed
 * `Intl.DateTimeFormat` — rather than `SidePanelNotificationList`'s richer
 * `getDateLabel`, keeping this helper self-contained instead of pulling in
 * a sibling component's formatting policy. The `toLocaleDateString` choice
 * specifically (over `new Intl.DateTimeFormat(...).format(...)`, which is
 * otherwise equivalent for a valid date) matters for a malformed
 * `clip.timestamp`: `Intl.DateTimeFormat().format(new Date(NaN))` THROWS
 * (`RangeError: Invalid time value`), which would blank the whole
 * clipboard screen render; `toLocaleDateString` degrades gracefully to the
 * string "Invalid Date" instead, same as its sibling.
 */
/**
 * The day axis for a clip. Module-level so its identity is stable across
 * renders — an inline arrow in the `useMemo` below would be a fresh value
 * each render, which is exactly the kind of dependency the exhaustive-deps
 * rule is right to complain about.
 */
const clipTimestamp = (clip: DockClip) => clip.timestamp;

function dayGroupLabel(group: DayGroup<DockClip>, labels: ClipboardScreenLabels): string {
  const formatted = new Date(group.date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  if (group.key === "today") return labels.today || formatted;
  if (group.key === "yesterday") return labels.yesterday || formatted;
  return formatted;
}

export function ClipboardScreen({
  clips,
  capabilities,
  labels,
  publicMode = false,
  onDeleteClip,
  onClearAll,
  onClipCopied,
  onComposerSend,
}: ClipboardScreenProps) {
  const [tip, setTip] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Two-step confirm for the destructive, irreversible clear-all — mirrors
  // SettingsPanel's disconnect confirm (`confirming` state, negative-ghost
  // trigger, negative commit).
  const [confirmingClearAll, setConfirmingClearAll] = useState(false);
  // True while readText() is awaiting OR a broadcast is in flight — gates
  // the paste control (so a fast double-click can't fire two reads, and so
  // pasting mid-send can't silently overwrite a draft that's already being
  // sent) AND the composer's Send button. Since C6 deleted the confirm
  // popover, this is the ONLY double-send protection: see
  // `handleComposerSend`.
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  // Per-send intent, set by the composer's lock toggle. Reset to off in the
  // same settle path that clears the draft (C7) — a sticky privacy flag
  // fails both ways: left on it mislabels ordinary clips, left off it
  // silently sends the next password unmasked.
  const [sensitive, setSensitive] = useState(false);

  // Midnight tick (C9): forces `dayGroups` below to re-derive at the
  // calendar-day boundary so "Today"/"Yesterday" headers stay accurate for
  // a panel left open across midnight, without navigating away and back.
  // Identical pattern to `useNotificationListView`'s own midnight tick for
  // the notification list's Today/Yesterday buckets — the state itself is
  // never read outside this effect/memo pair, only its identity change
  // matters as a memo-invalidation key.
  const [dayTick, setDayTick] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: dayTick triggers re-scheduling the next midnight timer
  useEffect(() => {
    const now = new Date();
    const msUntilMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
    const timer = setTimeout(() => setDayTick((t) => t + 1), msUntilMidnight + 100);
    return () => clearTimeout(timer);
  }, [dayTick]);

  // Round 3 (D5): re-mask every card when the document becomes hidden — a
  // one-click-forever reveal defeats the point of privacy mode the moment
  // the tab/panel goes out of sight. ONE listener here (not one per
  // ClipCard) bumps this counter, threaded to every card below as
  // `resetSignal`; only its identity change matters, same "just needs a
  // fresh value" contract as `dayTick`. Applies unconditionally to
  // `sensitive` clips and `publicMode` alike — no mode branching, matching
  // the Android clipboard manager's VM-held reveal reset on `ON_STOP`.
  const [hiddenTick, setHiddenTick] = useState(0);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) setHiddenTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Fix round (post-round-3 dogfood): `visibilitychange` alone leaves a
  // hole in the exact case D5 was written for. It misses an
  // unfocused-but-still-visible window — screen-share / alt-tab / a
  // standalone extension popup that isn't the active app, arguably the
  // MORE dangerous case since the content stays fully on screen — and the
  // extension's side panel is registered globally, so Chrome may never
  // mark it `hidden` on an ordinary browser-tab switch, which is D5's own
  // original motivating scenario. `useHasFocus` closes both holes with the
  // same `window` focus/blur signal `apps/extension/src/sidepanel/
  // useHasFocus.ts` already uses for the keyboard footer's
  // focus-awareness; that hook can't be imported here (this package has no
  // dependency on `apps/extension` — dependencies only run the other way),
  // so it's mirrored below rather than inventing a third focus/visibility
  // mechanism. Re-masking on ordinary focus loss (e.g. clicking out to
  // paste) is intentional, not a bug: this is the PC analogue of Android's
  // reveal reset on `ON_STOP` — privacy beats convenience.
  const hasFocus = useHasFocus();
  const wasFocusedRef = useRef(hasFocus);
  useEffect(() => {
    if (wasFocusedRef.current && !hasFocus) setHiddenTick((t) => t + 1);
    wasFocusedRef.current = hasFocus;
  }, [hasFocus]);

  // Tracks mount state so deferred setState (the toast timer) can't fire after
  // the screen unmounts — switching destinations unmounts it mid-flight.
  const mountedRef = useRef(true);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(toastTimer.current);
    };
  }, []);

  // A clip arriving clears any stale "clipboard is empty" tip — the state that
  // produced it no longer describes the world.
  const topId = clips[0]?.id;
  const prevTopId = useRef<string | undefined>(topId);
  useEffect(() => {
    const prev = prevTopId.current;
    prevTopId.current = topId;
    if (topId && topId !== prev) setTip(null);
  }, [topId]);

  // The clear-all confirm is only ever RENDERED while `clips.length > 0`, but
  // `confirmingClearAll` itself isn't reset just because the block disappears
  // — e.g. the user opens "Clear all", changes their mind, and deletes the
  // remaining clips individually instead. Without this, the flag stays true
  // and the next arriving clip would resurface the confirm step unprompted,
  // putting a destructive button in front of a user who never reopened it.
  useEffect(() => {
    if (clips.length === 0) setConfirmingClearAll(false);
  }, [clips.length]);

  const showToast = useCallback((text: string) => {
    if (!mountedRef.current) return;
    setTip(null);
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 2500);
  }, []);

  // Fills the draft from the OS clipboard under user activation. Folds the
  // retired standalone "Send clipboard" button's verb into this leading
  // composer control — it never sends by itself. The draft it fills is the
  // same field a typed send goes through, so what leaves the device is
  // always something the user can see (and edit, or abandon) first.
  const handlePasteFromClipboard = useCallback(async () => {
    setTip(null);
    setSending(true);
    try {
      const text = await capabilities.readText();
      if (!mountedRef.current) return;
      if (!text.trim()) {
        setToast(null);
        setTip(labels.emptyClipboardTip);
        return;
      }
      setToast(null);
      setDraft(text);
    } catch {
      // Read refused. Preserve whatever the user had already typed — the
      // recovery path is the field they're already looking at, not a wipe.
      if (mountedRef.current) {
        setToast(null);
        setTip(labels.readFailedTip);
      }
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [capabilities, labels.emptyClipboardTip, labels.readFailedTip]);

  // The composer's only send action (C6: one click, no confirm step).
  // Broadcasts immediately with the lock toggle's current value.
  //
  // `sending` is set synchronously before the broadcast starts, and is the
  // ONLY double-send guard now that the popover's `pending` gate is gone —
  // it disables the Send button (see the JSX) for the whole round trip, so a
  // second click while the first send is still in flight is a no-op. It is
  // deliberately NOT cleared until the broadcast settles (the `finally`
  // below), not eagerly on click, for the same reason `pending` used to stay
  // set: clearing it eagerly would re-enable Send (and the paste control)
  // while the first send hadn't finished, opening the door to a second
  // broadcast of the same plaintext, or a fresh paste silently clobbering
  // the draft mid-flight.
  //
  // The sensitive toggle resets only once the send settles, in this same
  // `finally` — C7: a sticky `sensitive` flag fails in both directions
  // (stale-on mislabels the next ordinary clip; stale-off sends the next
  // password unmasked), so per-send intent must not survive past the send
  // it was set for, success or failure alike.
  //
  // The DRAFT, unlike `sensitive`, is only cleared on a `delivered` send —
  // one click no longer sits behind a confirm step (C6), so a failed or
  // fully-undelivered broadcast must not also wipe text the user may not be
  // able to retype (a long token, a generated password, a URL copied from
  // another device). Mirrors the existing paste-refusal recovery rule
  // above: preserve what's already in the field rather than wiping it.
  const handleComposerSend = useCallback(() => {
    const text = draft;
    if (!text.trim() || sending) return;
    const wasSensitive = sensitive;
    setSending(true);
    void (async () => {
      let delivered = false;
      try {
        const r = await capabilities.broadcast(text, { sensitive: wasSensitive });
        delivered = r.total > 0 && r.sent > 0;
        showToast(delivered ? labels.sentToast(r.sent, r.total) : labels.sentNone);
        // Analytics only, matching Android's `enqueued > 0` success gate —
        // an event fires on a send that actually reached someone, not on
        // every submit.
        if (delivered) onComposerSend?.();
      } catch {
        showToast(labels.sentNone);
      } finally {
        if (mountedRef.current) {
          if (delivered) setDraft("");
          setSensitive(false);
          setSending(false);
        }
      }
    })();
  }, [draft, sending, sensitive, capabilities, labels, showToast, onComposerSend]);

  const handleClearAll = useCallback(() => {
    onClearAll?.();
    setConfirmingClearAll(false);
  }, [onClearAll]);

  // `Date.now()` read here (not inside `groupByDay`, which takes `now`
  // explicitly so it stays independently testable with fixed timestamps).
  // Memoized on `[clips, dayTick]` — `dayTick` isn't referenced in the body,
  // it's a deliberate extra dependency: its identity change (fired by the
  // midnight-tick effect above) is what forces recomputation at the
  // calendar-day boundary, same trick `useNotificationListView` uses for
  // `buildTimelineBuckets`. Computed once, ahead of the JSX, so both the
  // single-group header-suppression check below and the render loop read
  // the same array.
  const dayGroups = useMemo(
    () => groupByDay(clips, Date.now(), clipTimestamp),
    // biome-ignore lint/correctness/useExhaustiveDependencies: dayTick has no direct reference in the body — see the comment above.
    [clips, dayTick]
  );

  return (
    <section className={styles.screen} aria-label={labels.sectionLabel}>
      <div className={styles.column}>
        <div className={styles.recentHeader}>
          <Text size="xs" tone="tertiary" className={styles.recentHeading}>
            {labels.recentHeading}
          </Text>
          {/* Gated on the callback AND all three labels the control can
              render (trigger, confirm, cancel) — a host that wires
              `onClearAll` and `labels.clearAll` but omits `clearAllConfirm`
              or `clearAllCancel` gets no control at all (caught in
              review/QA) rather than a trigger that leads into a confirm step
              with a blank, unnamed but clickable DESTRUCTIVE button. The
              control fails closed as a unit, same as ClipCard's delete
              control above; this package isn't scanned by l10n:check, so a
              silent English/blank string would otherwise reach every
              locale. */}
          {clips.length > 0 &&
            onClearAll &&
            labels.clearAll &&
            labels.clearAllConfirm &&
            labels.clearAllCancel &&
            (confirmingClearAll ? (
              <div className={styles.clearAllConfirm}>
                <Button variant="negative" size="xs" onClick={handleClearAll}>
                  {labels.clearAllConfirm}
                </Button>
                <Button variant="ghost" size="xs" onClick={() => setConfirmingClearAll(false)}>
                  {labels.clearAllCancel}
                </Button>
              </div>
            ) : (
              // Neutral resting treatment (C8) — icon + label, same
              // `variant="ghost"` + `leadingIcon` idiom as the notification
              // toolbar's own clear-all
              // (`SidePanelNotificationList`'s `dismissAll` button). Only
              // the RESTING trigger changes; the confirm step's commit
              // button above stays `variant="negative"` — the destructive,
              // irreversible action itself is still clearly marked once the
              // user has actually chosen it.
              <Button
                variant="ghost"
                size="xs"
                leadingIcon={Trash01}
                onClick={() => setConfirmingClearAll(true)}
              >
                {labels.clearAll}
              </Button>
            ))}
        </div>

        <div className={styles.list} data-testid="clip-list">
          {clips.length === 0 ? (
            <div className={styles.empty}>
              <Text size="m" weight="semibold" tone="secondary">
                {labels.emptyTitle}
              </Text>
              <Text size="s" tone="tertiary">
                {labels.emptyBody}
              </Text>
            </div>
          ) : (
            // Day-group section headers (C9) — grouped via the pure
            // `groupByDay` helper. The header itself is suppressed when
            // there's only one group (`dayGroups.length > 1`), mirroring
            // `SidePanelNotificationList`'s `timelineBuckets.length > 1`
            // gate on its own date separator: most users will have only
            // today's clips, so an always-on lone "Today" header would be
            // exactly the always-visible Notification/clipboard divergence
            // this whole redesign exists to close, not an edge case.
            dayGroups.map((group) => (
              <Fragment key={`${group.key}-${group.date}`}>
                {dayGroups.length > 1 && (
                  <div className={styles.dayHeader}>
                    <Text size="xs" tone="tertiary" weight="medium" className={styles.dayLabel}>
                      {dayGroupLabel(group, labels)}
                    </Text>
                  </div>
                )}
                {group.items.map((c) => (
                  <ClipCard
                    key={c.id}
                    id={c.id}
                    content={c.content}
                    originLabel={c.originLabel}
                    timestamp={c.timestamp}
                    // Default an omitted direction to "received" — pre-existing
                    // hosts (not yet passing it, PR2) only ever showed inbound
                    // clips.
                    direction={c.direction ?? "received"}
                    sensitive={c.sensitive}
                    forceMask={publicMode}
                    resetSignal={hiddenTick}
                    provenance={c.provenance}
                    onCopy={(content) =>
                      capabilities.writeText(content).then(() => {
                        onClipCopied?.(c);
                      })
                    }
                    onDelete={onDeleteClip}
                    labels={labels.card}
                  />
                ))}
              </Fragment>
            ))
          )}
        </div>

        {/* Single shared live region, rendered only when it has content so an
            idle screen adds no empty role="status" node to the a11y tree.
            Toast supersedes tip. */}
        {(toast || tip) && (
          <Text size="xs" tone={toast ? "secondary" : "tertiary"} role="status">
            {toast ?? tip}
          </Text>
        )}

        <div className={styles.composer} data-testid="composer">
          <IconButton
            icon={Clipboard}
            label={labels.pasteFromClipboard}
            // m (28px) matches the adjacent size="s" Send button's 28px
            // min-height and the Input's xs (28px) — the default `s` (24px)
            // read as visibly out of step with the rest of the docked bar.
            size="m"
            // `sending` now covers both the OS-clipboard-read window AND a
            // send in flight (see the state docs above) — pasting fresh
            // content mid-send would silently clobber the draft the send
            // already captured.
            disabled={sending}
            onClick={() => void handlePasteFromClipboard()}
          />
          <div className={styles.composerField}>
            <Input
              label={labels.composerPlaceholder}
              hideLabel
              // xs (28px) matches the adjacent size="s" Send button's 28px
              // min-height (Input xs|sm|md vs Button xs|s|m).
              size="xs"
              placeholder={labels.composerPlaceholder}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              // Privacy mode: mask the draft like everything else on this
              // screen (ClipCard content). Display-only — `draft` itself is
              // unchanged, so the real typed/pasted text is still what gets
              // sent (`handleComposerSend` reads `draft`, never anything
              // from the DOM). No reveal toggle here by design — turning
              // privacy mode off is the escape hatch.
              masked={publicMode}
            />
          </div>
          {/* Gated on the label being present, same fail-closed idiom as
              the clear-all control above — a host that hasn't wired
              `markSensitive` yet gets a visibly missing control rather than
              an unlocalized English aria-label (this package isn't scanned
              by l10n:check). Icon swap (not a filled/pressed style) mirrors
              PublicModeToggle's Eye/EyeOff — `aria-pressed` carries the
              state for assistive tech. */}
          {labels.markSensitive && (
            <IconButton
              icon={sensitive ? Lock01 : LockUnlocked01}
              label={labels.markSensitive}
              size="m"
              disabled={sending}
              aria-pressed={sensitive}
              onClick={() => setSensitive((v) => !v)}
            />
          )}
          <Button
            variant="outline"
            size="s"
            // `sending` covers both "a send is in flight" and "a send just
            // settled but the draft/toast haven't caught up yet" (see the
            // state docs above) — the ONLY guard against a second click
            // re-broadcasting the same plaintext before the first send
            // finishes.
            disabled={!draft.trim() || sending}
            onClick={handleComposerSend}
          >
            {labels.composerSend}
          </Button>
        </div>
      </div>
    </section>
  );
}
